import Phaser from "phaser";
import { GAME } from "../config";
import { MultiplayerClient, getMpClient } from "../net/MultiplayerClient";
import { addFullscreenButton } from "../ui/Fullscreen";

/**
 * Лобби: создать комнату или войти по коду.
 * Мобильно-ориентированный UI: большие кнопки, явные digit boxes, без мелких шрифтов.
 *
 * Поддерживает auto-join по приглашению друга: init({mode:"join", autoCode:"123456"}).
 */
export class LobbyScene extends Phaser.Scene {
  private mp!: MultiplayerClient;
  private mode: "create" | "join" = "create";
  private statusText!: Phaser.GameObjects.Text;
  private joinInputCode = "";
  private digitBoxes: Phaser.GameObjects.Container[] = [];
  private mySlug?: string;
  private oppSlug?: string;
  private autoCode?: string;
  private mpUnsub?: () => void;

  constructor() { super("Lobby"); }

  init(data: { mode?: "create" | "join"; autoCode?: string }) {
    this.mode = data?.mode ?? "create";
    this.joinInputCode = "";
    this.digitBoxes = [];
    this.autoCode = data?.autoCode;
  }

  create() {
    const { WIDTH, HEIGHT } = GAME;

    const bg = this.add.graphics();
    bg.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    this.add.text(WIDTH / 2, 40, "ONLINE МАТЧ", {
      fontFamily: "Arial Black", fontSize: "32px",
      color: "#ffffff", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.statusText = this.add.text(WIDTH / 2, 90, "Подключение к серверу...", {
      fontFamily: "Arial Black", fontSize: "18px", color: "#ffd700",
      stroke: "#000", strokeThickness: 3,
    }).setOrigin(0.5);

    // Кнопка "назад" большая, видимая
    const backBtn = this.add.text(60, 30, "← НАЗАД", {
      fontFamily: "Arial Black", fontSize: "16px",
      color: "#ffffff", backgroundColor: "#444444",
      padding: { x: 12, y: 8 } as any,
    } as any).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    backBtn.on("pointerdown", () => this.exit());

    this.input.keyboard!.on("keydown-ESC", () => this.exit());

    addFullscreenButton(this);

    this.mp = getMpClient();
    this.mpUnsub = this.mp.on(e => this.handleEvent(e));

    const startFlow = () => {
      this.statusText.setText("Сервер подключен");
      if (this.autoCode && this.autoCode.length === 6) {
        // прыжок прямо в комнату по приглашению друга
        this.statusText.setText(`Подключение к ${this.autoCode}...`);
        this.statusText.setColor("#88ccff");
        this.mp.joinRoom(this.autoCode);
      } else if (this.mode === "create") {
        this.startCreate();
      } else {
        this.startJoin();
      }
    };

    if (this.mp.isOpen()) {
      startFlow();
    } else {
      this.mp.connect()
        .then(startFlow)
        .catch(() => {
          this.statusText.setColor("#ff4444");
          this.statusText.setText("Ошибка подключения. Назад →");
        });
    }
  }

  private startCreate() {
    this.mp.createRoom();
    this.statusText.setText("Создаём комнату...");
  }

  private startJoin() {
    this.statusText.setText("ВВЕДИ КОД КОМНАТЫ:");

    // 6 больших цифровых боксов
    this.makeDigitBoxes();

    // Большой numpad
    this.makeBigNumpad();

    // Подсказка
    this.add.text(GAME.WIDTH / 2, GAME.HEIGHT - 20,
      "📱 ТАП по цифрам     ⌨ печатай на клавиатуре     ↩ Enter — войти", {
      fontFamily: "Arial Black", fontSize: "12px", color: "#666666",
    }).setOrigin(0.5);

    // Hardware-keyboard
    this.input.keyboard!.on("keydown", (ev: KeyboardEvent) => {
      if (ev.key >= "0" && ev.key <= "9") {
        this.appendDigit(ev.key);
      } else if (ev.key === "Backspace") {
        this.popDigit();
      } else if (ev.key === "Enter" && this.joinInputCode.length === 6) {
        this.tryJoin();
      }
    });
  }

  private makeDigitBoxes() {
    const cx = GAME.WIDTH / 2;
    const cy = 140;
    const boxW = 56;
    const boxH = 72;
    const gap = 12;
    const totalW = boxW * 6 + gap * 5;
    const x0 = cx - totalW / 2 + boxW / 2;

    for (let i = 0; i < 6; i++) {
      const x = x0 + i * (boxW + gap);

      // Большой бокс с явной рамкой
      const box = this.add.rectangle(x, cy, boxW, boxH, 0x1a1a1a)
        .setStrokeStyle(3, 0x666666);

      // Цифра большая, явная — Arial Black, не monospace (которое съедает 0)
      const digit = this.add.text(x, cy, "", {
        fontFamily: "Arial Black", fontSize: "44px",
        color: "#ffd700", stroke: "#000", strokeThickness: 4,
      }).setOrigin(0.5);

      const container = this.add.container(0, 0, [box, digit]);
      (container as any)._box = box;
      (container as any)._digit = digit;
      this.digitBoxes.push(container);
    }
  }

  private refreshDigitBoxes() {
    for (let i = 0; i < 6; i++) {
      const box = this.digitBoxes[i];
      const digitText = (box as any)._digit as Phaser.GameObjects.Text;
      const boxRect = (box as any)._box as Phaser.GameObjects.Rectangle;

      const ch = this.joinInputCode[i] ?? "";
      digitText.setText(ch);

      // Подсветка активного бокса
      const isActive = i === this.joinInputCode.length;
      const isFilled = i < this.joinInputCode.length;
      if (isActive) boxRect.setStrokeStyle(4, 0xffd700);
      else if (isFilled) boxRect.setStrokeStyle(3, 0x88ff88);
      else boxRect.setStrokeStyle(3, 0x666666);
    }
  }

  private makeBigNumpad() {
    // Numpad помещаем целиком в HEIGHT=540: 4 ряда × (btnSize+gap) + верхний оффсет.
    // Стартуем с y = 230 (под digit boxes снизу на 210), нижний ряд y = 230 + 3*(58+8) = 428,
    // его нижняя кромка = 428 + 29 = 457 → подсказка на y = 500 свободно помещается.
    const cx = GAME.WIDTH / 2;
    const btnSize = 58;
    const gap = 8;
    const cyTop = 230;
    const cy = cyTop + btnSize / 2;
    const layout = [
      ["1","2","3"],
      ["4","5","6"],
      ["7","8","9"],
      ["⌫","0","✓"],
    ];

    layout.forEach((row, ri) => {
      row.forEach((label, ci) => {
        const x = cx + (ci - 1) * (btnSize + gap);
        const y = cy + ri * (btnSize + gap);

        let bgColor = 0x2a2a2a;
        let textColor = "#ffffff";
        if (label === "✓") { bgColor = 0x2a6a2a; textColor = "#88ff88"; }
        if (label === "⌫") { bgColor = 0x6a2a2a; textColor = "#ff8888"; }

        const btn = this.add.rectangle(x, y, btnSize, btnSize, bgColor)
          .setStrokeStyle(3, 0xffffff, 0.6)
          .setInteractive({ useHandCursor: true });

        const txt = this.add.text(x, y, label, {
          fontFamily: "Arial Black",
          fontSize: label === "⌫" || label === "✓" ? "24px" : "30px",
          color: textColor,
          stroke: "#000", strokeThickness: 3,
        }).setOrigin(0.5);

        btn.on("pointerdown", () => {
          btn.setFillStyle(bgColor + 0x202020);
          if (label === "⌫") this.popDigit();
          else if (label === "✓") this.tryJoin();
          else this.appendDigit(label);
        });
        btn.on("pointerup", () => btn.setFillStyle(bgColor));
        btn.on("pointerout", () => btn.setFillStyle(bgColor));
      });
    });
  }

  private appendDigit(d: string) {
    if (this.joinInputCode.length >= 6) return;
    this.joinInputCode += d;
    this.refreshDigitBoxes();
  }

  private popDigit() {
    if (this.joinInputCode.length === 0) return;
    this.joinInputCode = this.joinInputCode.slice(0, -1);
    this.refreshDigitBoxes();
  }

  private tryJoin() {
    if (this.joinInputCode.length !== 6) {
      this.statusText.setColor("#ff8888");
      this.statusText.setText("Введи все 6 цифр!");
      return;
    }
    this.statusText.setColor("#88ccff");
    this.statusText.setText(`Подключение к ${this.joinInputCode}...`);
    this.mp.joinRoom(this.joinInputCode);
  }

  private handleEvent(e: any) {
    switch (e.type) {
      case "created": {
        this.statusText.setText("ПЕРЕДАЙ ДРУГУ КОД:");

        // Большой код в шесть боксов аналогично join
        const cx = GAME.WIDTH / 2;
        const cy = 200;
        const boxW = 90;
        const boxH = 110;
        const gap = 14;
        const totalW = boxW * 6 + gap * 5;
        const x0 = cx - totalW / 2 + boxW / 2;

        for (let i = 0; i < 6; i++) {
          const x = x0 + i * (boxW + gap);
          this.add.rectangle(x, cy, boxW, boxH, 0x1a1a1a)
            .setStrokeStyle(4, 0xffd700);
          this.add.text(x, cy, this.mp.roomCode![i], {
            fontFamily: "Arial Black", fontSize: "72px",
            color: "#ffd700", stroke: "#c41e3a", strokeThickness: 5,
          }).setOrigin(0.5);
        }

        this.add.text(cx, cy + 100, "Скажи или скинь этот код другу", {
          fontFamily: "Arial Black", fontSize: "16px", color: "#aaaaaa",
        }).setOrigin(0.5);

        // Копировать
        const copyBtn = this.add.rectangle(cx, cy + 160, 280, 50, 0x2a4a6a)
          .setStrokeStyle(2, 0x88ccff)
          .setInteractive({ useHandCursor: true });
        const copyTxt = this.add.text(cx, cy + 160, "📋 СКОПИРОВАТЬ КОД", {
          fontFamily: "Arial Black", fontSize: "18px", color: "#ffffff",
        }).setOrigin(0.5);
        copyBtn.on("pointerdown", () => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(this.mp.roomCode!).then(() => {
              copyTxt.setText("✓ СКОПИРОВАНО");
              this.time.delayedCall(1500, () => copyTxt.setText("📋 СКОПИРОВАТЬ КОД"));
            }).catch(() => {});
          }
        });

        this.add.text(cx, GAME.HEIGHT - 70, "🕐 Ждём оппонента...", {
          fontFamily: "Arial Black", fontSize: "20px", color: "#ffaa00",
          stroke: "#000", strokeThickness: 3,
        }).setOrigin(0.5);
        break;
      }

      case "joined":
        this.statusText.setText("Вошёл в комнату ✓");
        this.statusText.setColor("#88ff88");
        break;

      case "opponent_ready":
        this.statusText.setText("СОПЕРНИК НАЙДЕН! → выбор бойца");
        this.statusText.setColor("#88ff88");
        this.time.delayedCall(800, () => {
          this.scene.start("Select", { online: true, mp: this.mp });
        });
        break;

      case "error":
        this.statusText.setColor("#ff4444");
        this.statusText.setText(`✗ ${e.msg}`);
        break;

      case "opponent_left":
        this.statusText.setColor("#ff4444");
        this.statusText.setText("Соперник отключился");
        break;
    }
  }

  private exit() {
    // не закрываем глобальный mp client — только отвязываем listener и комнату
    this.mpUnsub?.();
    this.mp?.leaveRoom();
    this.scene.start("MainMenu");
  }
}
